import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { RadioListStyle } from '@ftr/forms'
import { ButtonColor, ButtonDisplayType, DestroySubscribers } from '@ftr/foundation'
import { BehaviorSubject, Observable, isObservable, takeUntil, tap } from 'rxjs'
import { ComplexFilter, ComplexFilterData, ComplexFilterSection, NestingDisplayMode } from './ComplexFilter'
import { ComplexFilterService, filtersApplied } from './complex-filter.service'

type FilterToggleState = Record<string, BehaviorSubject<boolean>>

/**
 * Complex filter component shows a complex filter based on filter configuration provided.
 */
@Component({
  selector: 'ftr-complex-filter',
  templateUrl: './complex-filter.component.html',
  styleUrls: ['./complex-filter.component.css'],
})
export class ComplexFilterComponent extends DestroySubscribers implements OnInit {
  @Input() config: ComplexFilterSection[]
  /**
   * Determines whether we should apply selected values immediately or wait for the user to hit Apply
   */
  @Input() applyImmediately: boolean
  @Input() visibilityToggled: Observable<boolean>
  @Input() defaultExpanded: boolean = false
  @Input() nestingDisplayMode: NestingDisplayMode = 'nested'
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()
  @Output() filterCleared = new EventEmitter<ComplexFilterData>()

  readonly radioListStyle = RadioListStyle
  readonly buttonDisplayType = ButtonDisplayType
  readonly buttonColor = ButtonColor
  readonly isObservable = isObservable

  internalFormGroup: UntypedFormGroup = new UntypedFormGroup({})
  filters: ComplexFilter[]
  /**
   * Keeps track of each filter's toggle state
   */
  filterToggleState: FilterToggleState = {}
  /**
   * Whether this is the first load of this component
   */
  initialLoad = true

  filtersApplied = (filterKey: string): number => filtersApplied(this.internalFormGroup.value, [], [], filterKey)

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly complexFilterService: ComplexFilterService,
  ) {
    super()
  }

  ngOnInit(): void {
    this.filters = this.complexFilterService.getFilters(this.config)
    this.buildInternalFormControls()
    this.listenToFilterChange()
    this.setupToggleState()
  }

  async applyFilters(): Promise<void> {
    // This stops the page param from being wiped out on first load
    await this.complexFilterService.updateFilter(this.internalFormGroup.value, { resetPage: !this.initialLoad })

    this.filterChanged.emit(this.internalFormGroup.value)
    this.initialLoad = false
  }

  async clearAll(): Promise<void> {
    await this.complexFilterService.clearAll(this.filters)
    this.filterCleared.emit(this.internalFormGroup.value)
  }

  registerFilterChange(filter: ComplexFilterData): void {
    // Patch the form and emit if applying immediately
    this.internalFormGroup.patchValue(filter, { emitEvent: this.applyImmediately })
  }

  setupToggleState(): void {
    const initialToggleState = this.defaultExpanded
    // Initialize toggle states for each filter
    this.filters.forEach(({ filterKey }) => {
      this.filterToggleState[filterKey] = new BehaviorSubject(initialToggleState)
    })

    /* Get an update only when this component is toggled on,
       we don't want the filter to collapse when the user clears it */
    this.visibilityToggled.pipe(takeUntil(this.finalize)).subscribe(toggled => {
      if (toggled) {
        this.filters.forEach(({ filterKey }) => {
          const value = this.internalFormGroup.controls[filterKey].value
          this.filterToggleState[filterKey].next(
            initialToggleState ? initialToggleState : Array.isArray(value) ? !!value.length : !!value,
          )
        })
      }
    })
  }

  toggleFilter(filterKey: string, value: boolean): void {
    this.filterToggleState[filterKey].next(value)
  }

  isMultiSelectFilter(filter: ComplexFilter): boolean {
    return filter.type === 'checkbox-list' || filter.type === 'checkbox-tree'
  }

  private buildInternalFormControls(): void {
    this.filters.forEach(filter => {
      this.internalFormGroup.addControl(filter.filterKey, this.formBuilder.control(null))
    })

    this.internalFormGroup.valueChanges
      .pipe(
        takeUntil(this.finalize),
        tap(async () => {
          if (this.complexFilterService.isClearAll) {
            this.filterCleared.emit(this.internalFormGroup.value)
            return
          }
          await this.applyFilters()
        }),
      )
      .subscribe()
  }

  private listenToFilterChange(): void {
    this.complexFilterService
      .listenToFilterChange(this.filters)
      .pipe(takeUntil(this.finalize))
      .subscribe(filters => {
        // Patch the form and emit
        this.internalFormGroup.patchValue(filters)
      })
  }
}
